/** 既存ノートの読み取り専用個別画面（docs/01-requirements.md §5.18）。 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api, ApiError } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { ErrorNotice } from '@app/components/ErrorNotice';
import { Icon } from '@app/components/icons';
import { MarkdownView } from '@app/components/MarkdownView';
import { subjectLabel } from '@app/components/subject-label';
import { useCategories } from '@app/components/useCategories';
import { getDraft, type DraftRecord } from '@app/storage/draft-buffer';
import { displayTitle } from '@shared/markdown-input';
import type { Note } from '@shared/types';
import { resolveNoteView, type NoteViewResolution } from '@app/views/note-view';
import { resolveBackNavigation } from '@app/views/back-navigation';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; resolution: NoteViewResolution; headError: unknown | null }
  | { kind: 'not_found' }
  | { kind: 'error'; error: unknown };

export function NoteViewPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const location = useLocation();
  const categories = useCategories();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  const loadNote = useCallback(() => {
    if (!noteId) {
      setLoad({ kind: 'not_found' });
      return;
    }

    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const draftPromise = getDraft(noteId).catch(() => undefined);
      let head: Note | null = null;
      let headError: unknown = null;
      try {
        head = await api.getNote(noteId);
      } catch (error: unknown) {
        headError = error;
      }
      const draft: DraftRecord | null = (await draftPromise) ?? null;
      if (cancelled) return;

      const resolution = resolveNoteView(head, draft);
      if (resolution) {
        setLoad({ kind: 'ready', resolution, headError });
      } else if (headError instanceof ApiError && headError.code === 'not_found') {
        setLoad({ kind: 'not_found' });
      } else {
        setLoad({ kind: 'error', error: headError ?? new Error('ノートを開けませんでした') });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [noteId]);

  useEffect(() => loadNote(), [loadNote]);

  if (load.kind === 'loading') {
    return <div className="centered-message">読み込み中…</div>;
  }

  if (load.kind === 'not_found') {
    return (
      <div className="centered-message">
        <p>このノートは見つかりませんでした。</p>
        <Link className="al-button al-button--primary" to="/">
          タイムラインへ
        </Link>
      </div>
    );
  }

  if (load.kind === 'error') {
    return (
      <div className="centered-message">
        <ErrorNotice error={load.error} onRetry={loadNote} />
        <Link className="al-button al-button--secondary" to="/">
          タイムラインへ
        </Link>
      </div>
    );
  }

  const { content, source } = load.resolution;
  const categoryName = categories.nameOf(content.category_id);
  const back = resolveBackNavigation(location.key, location.state, '/');

  return (
    <div className="page">
      <AppHeader
        title="ノート"
        backTo={back.to}
        backReplace={back.replace}
        backState={back.state}
        right={
          <Link
            className="icon-button"
            to={`/notes/${noteId}/edit`}
            aria-label="編集"
            title="編集"
          >
            <Icon name="edit" />
          </Link>
        }
      />

      <main className="page__body note-view">
        {load.headError != null && <ErrorNotice error={load.headError} onRetry={loadNote} />}
        {source === 'local_draft' && (
          <p className="al-notice al-notice--cream notice-block">
            {load.headError != null
              ? 'サーバーの最新版を確認できないため、この端末の未送信内容を表示しています。'
              : 'この端末の未送信内容を表示しています。'}
          </p>
        )}
        {source === 'head_with_orphan_draft' && (
          <p className="al-notice al-notice--cream notice-block">
            別の端末で更新されているため、この端末の未送信内容は本文へ反映していません。編集画面を開くと履歴へ退避します。
          </p>
        )}

        <header className="note-view__header">
          <h2 className="note-view__title">{displayTitle(content.title)}</h2>
          <div className="note-view__meta">
            <span>{subjectLabel(content.subject_date, content.subject_date_end)}</span>
            {categoryName && (
              <span className="al-chip al-chip--lilac mm-category-badge note-view__category">
                {categoryName}
              </span>
            )}
          </div>
        </header>

        {content.body === '' ? (
          <p className="muted note-view__empty">本文はありません。</p>
        ) : (
          <MarkdownView source={content.body} className="note-view__body" />
        )}

        {categories.error != null && <ErrorNotice error={categories.error} />}
      </main>
    </div>
  );
}
